import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Trash2 } from 'lucide-react'
import { useDeleteCampaign } from './use-campaigns'

type DeleteCampaignDialogProps = {
  readonly campaignName: string
  readonly campaignId: string
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
  readonly onDeleted: () => void
}

export function DeleteCampaignDialog({
  campaignName,
  campaignId,
  open,
  onOpenChange,
  onDeleted,
}: DeleteCampaignDialogProps) {
  const [confirmText, setConfirmText] = useState('')
  const deleteCampaign = useDeleteCampaign()

  const matches = confirmText.trim() === campaignName.trim()

  function handleDelete() {
    if (!matches) return
    deleteCampaign.mutate(campaignId, {
      onSuccess: () => {
        setConfirmText('')
        onOpenChange(false)
        onDeleted()
      },
    })
  }

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) setConfirmText('')
    onOpenChange(nextOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-destructive flex items-center gap-2">
            <Trash2 className="h-5 w-5" />
            Delete Campaign
          </DialogTitle>
          <DialogDescription>
            The campaign will be moved to the Deleted tab and its outreach
            stops. Its leads are unassigned but not deleted, so you can add
            them to another campaign.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3 py-2">
          <Label htmlFor="confirm-name">
            Type <span className="font-semibold text-foreground">{campaignName}</span> to confirm
          </Label>
          <Input
            id="confirm-name"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={campaignName}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && matches) handleDelete()
            }}
            autoFocus
          />
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={deleteCampaign.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={!matches || deleteCampaign.isPending}
          >
            {deleteCampaign.isPending ? 'Deleting...' : 'Delete Campaign'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
